import React, { useCallback, useEffect, useRef, useState } from 'react';
import { designGalleryImages } from '../designGallery/loadDesignGalleryImages';
import { designGalleryPublicMedia } from '../designGallery/designGalleryPublicMedia';
import DesignGalleryVideo from './DesignGalleryVideo';

/** Hold pointer here this long before the frame zooms (ms); ~0 feels instant */
const HOVER_ZOOM_MS = 0;

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function DesignGalleryFigure({ item }) {
  const [zoomed, setZoomed] = useState(false);
  const timerRef = useRef(null);

  const clearZoomTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearZoomTimer(), [clearZoomTimer]);

  const onPointerEnter = useCallback(() => {
    clearZoomTimer();
    if (prefersReducedMotion()) return;
    if (HOVER_ZOOM_MS <= 0) {
      setZoomed(true);
      return;
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setZoomed(true);
    }, HOVER_ZOOM_MS);
  }, [clearZoomTimer]);

  const onPointerLeave = useCallback(() => {
    clearZoomTimer();
    setZoomed(false);
  }, [clearZoomTimer]);

  const figureClasses = [
    'design-gallery__item',
    item.kind === 'video' ? 'design-gallery__item--video' : '',
    item.size === 'large' ? 'design-gallery__item--large' : '',
    zoomed ? 'design-gallery__item--zoom-active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const frameClasses = [
    'design-gallery__frame',
    zoomed ? 'design-gallery__frame--zoomed' : '',
    zoomed && item.kind === 'video' ? 'design-gallery__frame--zoomed-video' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <figure
      className={figureClasses}
      role="listitem"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <div className={frameClasses}>
        {item.kind === 'video' ? (
          <DesignGalleryVideo
            src={item.src}
            alt={item.alt}
            loopStart={item.loopStart}
            loopEnd={item.loopEnd}
          />
        ) : (
          <img src={item.src} alt={item.alt} loading="lazy" decoding="async" />
        )}
        {item.hoverText ? (
          <figcaption className="design-gallery__overlay" aria-hidden>
            {item.hoverText}
          </figcaption>
        ) : null}
      </div>
    </figure>
  );
}

export default function DesignGallery() {
  const [items, setItems] = useState(() => [
    ...designGalleryImages.map((img) => ({ kind: 'image', ...img })),
    ...designGalleryPublicMedia.map((entry) => ({ kind: entry.type === 'video' ? 'video' : 'image', ...entry })),
  ]);

  useEffect(() => {
    // Shuffle on mount so a hard refresh always scrambles ordering.
    setItems((prev) => shuffleInPlace([...prev]));
  }, []);

  if (!items.length) {
    return (
      <div className="design-gallery design-gallery--empty">
        <p>
          Add image files to <span className="design-gallery__path">src/assets/design-gallery</span> (PNG, JPG,
          WebP, GIF, or SVG), or large GIFs/videos under{' '}
          <span className="design-gallery__path">public/images/design-gallery</span> (registered in{' '}
          <span className="design-gallery__path">src/designGallery/designGalleryPublicMedia.js</span>), then save
          and refresh the dev server.
        </p>
      </div>
    );
  }

  return (
    <div className="design-gallery design-gallery__masonry" role="list" data-aos="fade-up" data-aos-once>
      {items.map((item) => (
        <DesignGalleryFigure key={`${item.kind}-${String(item.src)}`} item={item} />
      ))}
    </div>
  );
}
